
import type { GameData } from '@/hooks/use-game-data';

interface LegacyGameData {
  bestScore?: number;
  totalGamesPlayed?: number;
  currentStreak?: number;
  lastPlayedDate?: string;
  unlockedFacts?: string[];
  unlockedLevels?: number[];
  completedGrids?: Record<string, boolean>;
  dailyChallengeCompleted?: boolean;
  // Old format variations
  score?: number;
  gamesPlayed?: number;
  streak?: number;
  facts?: string[];
  levels?: number[];
}

/**
 * Attempts to recover game data from various localStorage keys
 */
export const recoverLegacyGameData = (): GameData | null => {
  const possibleKeys = [
    'treasures_djibouti_guest',
    'gameData',
    'treasures_game_data',
    'djibouti_game',
    'user_game_progress'
  ];

  console.log('🔍 Game Recovery - Searching for legacy data...');

  for (const key of possibleKeys) {
    try {
      const rawData = localStorage.getItem(key);
      if (rawData) {
        console.log(`🔍 Game Recovery - Found data in key: ${key}`);
        
        const parsedData = JSON.parse(rawData) as LegacyGameData;
        
        // Convert legacy format to current format
        const recoveredData: GameData = {
          bestScore: parsedData.bestScore || parsedData.score || 0,
          totalGamesPlayed: parsedData.totalGamesPlayed || parsedData.gamesPlayed || 0,
          currentStreak: parsedData.currentStreak || parsedData.streak || 0,
          lastPlayedDate: parsedData.lastPlayedDate || '',
          unlockedFacts: parsedData.unlockedFacts || parsedData.facts || [],
          unlockedLevels: parsedData.unlockedLevels || parsedData.levels || [1],
          completedGrids: parsedData.completedGrids || {},
          dailyChallengeCompleted: parsedData.dailyChallengeCompleted || false
        };

        // Only return if we found meaningful data
        if (recoveredData.bestScore > 0 || recoveredData.totalGamesPlayed > 0) {
          console.log('✅ Game Recovery - Successfully recovered data:', recoveredData);
          return recoveredData;
        }
      }
    } catch (error) {
      console.log(`❌ Game Recovery - Error parsing data from ${key}:`, error);
    }
  }

  console.log('❌ Game Recovery - No recoverable data found');
  return null;
};

/**
 * Cleans up old localStorage keys after successful migration
 */
export const cleanupLegacyData = (): void => {
  const keysToClean = [
    'gameData',
    'treasures_game_data', 
    'djibouti_game',
    'user_game_progress'
  ];

  keysToClean.forEach(key => {
    if (localStorage.getItem(key)) {
      console.log(`🧹 Game Recovery - Cleaning up old key: ${key}`);
      localStorage.removeItem(key);
    }
  });
};

/**
 * Manual data entry helper
 */
export const createManualGameData = (
  bestScore: number,
  totalGamesPlayed: number,
  currentStreak: number = 0
): GameData => {
  return {
    bestScore,
    totalGamesPlayed,
    currentStreak,
    lastPlayedDate: new Date().toISOString().split('T')[0],
    unlockedFacts: [],
    unlockedLevels: [1],
    completedGrids: {},
    dailyChallengeCompleted: false
  };
};
